import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Grid, ListItemAvatar, ListItemText, ListItemSecondaryAction, Tooltip } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import {
	ButtonLink,
	FormattedDateTimeRange,
	Icon,
	ImageAvatar,
	SquareChip,
	TagsArray,
	Text,
	useSettings
} from 'app/main/components';
import buttons from './LeaveButtons';

export default function LeaveListItem(props: ItemLayoutProps) {
	const { t } = useSettings();

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="users"
			uid={props.model.leav_user_uid}
			avatar={props.model.user_avatar}
			initials={props.model.leav_title_initials}
			labelBadge={props.model.lvst_title_initials}
			labelBadgeTooltip={props.model.lvst_title}
			badgeColor={props.model.lvst_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={{ xs: props.width >= 960 ? 7 : 12 }}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.leav_warning_desc && (
								<Tooltip title={props.model.leav_warning_desc} placement="bottom" arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.leav_blocked_desc && (
								<Tooltip title={props.model.leav_blocked_desc} placement="bottom" arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'leave', uid: props.model.leav_uid }}
							>
								{props.model.user_title && `${props.model.user_title} - `}
								{props.model.leav_title}
							</Text>
						</Grid>
						<Grid size={12} className="inline-flex items-center">
							{/* DESCRIPTION */}
							{props.model.leav_description && (
								<Text
									sx={{
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis'
									}}
								>
									{props.model.leav_description}
								</Text>
							)}
						</Grid>
						{/* TYPE */}
						<Grid>
							{props.model.lvty_title && (
								<SquareChip label={props.model.lvty_title} backgroundColor={props.model.lvty_color} />
							)}
						</Grid>
						{/* DATE FROM - TO */}
						<Grid>
							<SquareChip
								label={
									props.model.leav_d_from && props.model.leav_d_to ? (
										<FormattedDateTimeRange
											from={props.model.leav_d_from}
											to={props.model.leav_d_to}
											displayTime
										/>
									) : (
										<></>
									)
								}
							/>
						</Grid>
						{/* RECOVERY FROM - TO */}
						{!!props.model.leav_recovery_hours &&
						!!props.model.leav_recovery_d_from &&
						!!props.model.leav_recovery_d_to ? (
							<Grid>
								<SquareChip
									label={
										<FormattedMessage
											id="leaveListItem.recoveryHours"
											defaultMessage={t('VIEW_LEAVE_ITEM_RECOVERY_HOURS')}
											values={{
												dateRange: (
													<FormattedDateTimeRange
														from={props.model.leav_recovery_d_from}
														to={props.model.leav_recovery_d_to}
														displayTime
													/>
												)
											}}
										/>
									}
								/>
							</Grid>
						) : (
							<></>
						)}
						{/* TAGS */}
						<Grid>
							<TagsArray tags={props.model.leav_tags} changeFilter={props.changeFilter} />
						</Grid>
					</Grid>
				</Grid>
				{props.width >= 720 && (
					<Grid size={4}>
						{/* USER ASSIGNED */}
						<ButtonLink
							href={`/users/details/${props.model.leav_user_assigned_uid}`}
							icon="account-circle"
							fallback={t('VIEW_ITEM_NO_INTREF')}
						>
							{props.model.user_assigned_title}
						</ButtonLink>
					</Grid>
				)}
				{/* TAGS */}
				<TagsArray tags={props.model.leav_tags} changeFilter={props.changeFilter} />
			</Grid>
		</GridListItem>
	);
}
